import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  Box,
  Button,
  ButtonProps,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@material-ui/core';

import { useDebounce } from '../Debounce.hooks';
import { PaginatedList } from '../PaginatedList.interface';

/**
 * Context for storing selected items that can be used by shared components like
 * SelectDialogTable and SelectDialogSubmitButton.
 */
export const SelectDialogContext = createContext<{
  selectedItems: any[];
  setSelectedItems: (value: React.SetStateAction<any[]>) => void;
}>({
  selectedItems: [],
  setSelectedItems: (value: any) => {}
});

/**
 * Dialog wrapper and SelectDialogContext setup.
 */
const SelectDialog: React.FC<{
  open: boolean;
  selectedItems?: any[];
  onClose?: () => void;
}> = ({
  open,
  onClose,
  children,
  selectedItems: initialSelectedItems = []
}) => {
  const [selectedItems, setSelectedItems] = useState<any[]>([]);

  useEffect(() => {
    if (initialSelectedItems) {
      setSelectedItems(initialSelectedItems);
    }
  }, [initialSelectedItems]);

  return (
    <SelectDialogContext.Provider value={{ selectedItems, setSelectedItems }}>
      <Dialog open={open} onClose={onClose}>
        {children}
      </Dialog>
    </SelectDialogContext.Provider>
  );
};

export default SelectDialog;

/**
 * The search field for for the SelectDialog
 */
export const SelectDialogSearchField: React.FC<{
  label?: string;
  onSearch?: (value: string) => void;
}> = ({ label, onSearch }) => {
  const onSearchRef = useRef(onSearch);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search || '', 300);
  useEffect(() => {
    // handle searching
    onSearchRef?.current?.(debouncedSearch);
  }, [debouncedSearch]);
  return (
    <Box mt={1} mb={2} className='search-box'>
      <form autoComplete='off' onSubmit={event => event.preventDefault()}>
        <TextField
          variant='outlined'
          autoFocus
          fullWidth
          autoComplete='off'
          type='text'
          name='search'
          label={label}
          onChange={event => setSearch(event.target.value)}
        />
      </form>
    </Box>
  );
};

export interface SelectDialogTableColumn {
  /**
   * The property key name in the table data.
   */
  key: string;
  /**
   * The column human friendly name.
   */
  label?: string;
  /**
   * Add wrapped styling for the table cell (for long texts).
   */
  wrap?: boolean;
  /**
   * Provide a render override (optional).
   */
  render?: (item: any) => JSX.Element | string | null | undefined;
}

/**
 * The table view for the SelectDialog.
 */
export const SelectDialogTable: React.FC<{
  /**
   * The property key in the data to use as an ID when matching records.
   */
  idKey: string;
  /**
   * The column definition for the table.
   */
  columns: SelectDialogTableColumn[];
  /**
   * The data for the table.
   */
  dataList?: PaginatedList<any>;
  /**
   * Loading state.
   */
  isLoading?: boolean;
  /**
   * Allow for multiple selections
   */
  multiple?: boolean;
  /**
   * Drop down selection of number of rows to display
   */
  rowsPerPageOptions?: number[];
  /**
   * current page in table pagination
   */
  page?: number;
  /**
   * Number of rows in table to display
   */
  rowsPerPage?: number;
  /**
   * Event when page is changed in table pagination
   */
  onChangePage?: (page: number) => void;
  /**
   * Event when rows per page option is changed
   */
  onChangeRowsPerPage?: (limit: number) => void;
}> = ({
  idKey,
  columns,
  dataList,
  isLoading,
  multiple,
  rowsPerPageOptions = [5, 10, 25],
  page = 0,
  rowsPerPage = 5,
  onChangePage,
  onChangeRowsPerPage
}) => {
  const { selectedItems, setSelectedItems } = useContext(SelectDialogContext);
  const isSelected = useCallback(
    (item: any) => {
      // check if a item is selected
      return selectedItems.some(i => i[idKey] === item[idKey]);
    },
    [idKey, selectedItems]
  );
  const filteredItems = useMemo(() => {
    // remove any selected items since selected items are displayed above the
    // search list
    if (!dataList) return [];
    return dataList.data.filter(item => !isSelected(item));
  }, [dataList, isSelected]);

  const renderRows = (items: any[]) => {
    return items.map(item => (
      <TableRow
        key={item[idKey]}
        hover
        role='checkbox'
        color='primary'
        selected={isSelected(item)}
        onClick={() => {
          if (isSelected(item)) {
            setSelectedItems(
              selectedItems.filter(i => i[idKey] !== item[idKey])
            );
          } else {
            if (multiple) {
              setSelectedItems([...selectedItems, item]);
            } else {
              setSelectedItems([item]);
            }
          }
        }}
      >
        {multiple && (
          <TableCell padding='checkbox'>
            <Checkbox color='primary' checked={isSelected(item)} />
          </TableCell>
        )}
        {columns.map(column => (
          <TableCell
            key={column.key}
            style={
              column.wrap
                ? {
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    textOverflow: 'ellipsis'
                  }
                : undefined
            }
          >
            {column.render ? column.render(item) : item[column.key]}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {multiple && <TableCell></TableCell>}
              {columns.map(column => (
                <TableCell key={column.key}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={columns.length} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {renderRows(selectedItems)}
            {renderRows(filteredItems)}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component='div'
        count={dataList?.total || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onChangePage?.(newPage)}
        onRowsPerPageChange={event => {
          onChangeRowsPerPage?.(+event.target.value);
          onChangePage?.(0);
        }}
      />
    </Card>
  );
};

/**
 * Submit button used by the SelectDialog to retrieve the `selectedItems` from
 * the `SelectDialogContext`.
 */
export const SelectDialogSubmitButton: React.FC<
  Omit<ButtonProps, 'onSubmit'> & {
    isSubmitting?: boolean;
    onSubmit?: (items: any[]) => void;
  }
> = ({ isSubmitting, onSubmit, children, variant, color, ...buttonProps }) => {
  const { selectedItems } = useContext(SelectDialogContext);
  return (
    <Button
      variant={variant || 'contained'}
      color={color || 'primary'}
      // don't disable button (allow unassigning all items)
      // disabled={selectedItems.length === 0}
      endIcon={isSubmitting && <CircularProgress color='primary' size={20} />}
      onClick={() => onSubmit?.(selectedItems)}
      {...buttonProps}
    >
      {children}
    </Button>
  );
};
